/**
 * Pure read helpers for the Kautionen & Sicherheiten page.
 *
 * TWO SIDES OF ONE GERMAN WORD, KEPT APART BY CONSTRUCTION
 * -------------------------------------------------------
 * "Kaution" names two opposite things in this product, and the whole point of
 * this module is that no function here can accidentally add them together:
 *
 *   TENANT  `/deposits/tenant-held`      what tenants placed with US  — a liability
 *   LANDLORD `/deposits/landlord-placed` what WE placed with owners   — an asset
 *
 * Every selector below reads exactly one of the two payloads. There is
 * deliberately no helper that takes both, because the only thing such a helper
 * could usefully return is a sum, and that sum is meaningless.
 *
 * A SECURITY IS NOT A CASH BALANCE
 * --------------------------------
 * Part of the tenant total is insurance: a guarantee sum an insurer would pay,
 * money that never moved. K1 had to correct exactly this confusion on the
 * landlord side, where "Gebundenes Kapital" reported guarantees as tied
 * liquidity. The KPI labels carry the qualifier so the page cannot repeat it.
 *
 * PREMIUMS ARE A THIRD DIMENSION
 * ------------------------------
 * Both payloads carry an `annual_premium_total_chf`, and they mean different
 * things: the landlord one is OUR operating cost, the tenant one is what the
 * tenant pays their own insurer. Neither is ever part of a security total, and
 * the two are never interchanged — `selectLandlordDepositKpis` is the only
 * selector that surfaces a premium.
 */

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** Share of `part` in `whole`, as 0..100, or null when it cannot be stated. */
function share(part, whole) {
  if (part === null || whole === null || whole <= 0 || part <= 0) return null;
  return (part / whole) * 100;
}

/**
 * The three tenant KPI figures plus the context each one needs.
 *
 * `cashShare` / `insuranceShare` are shares of the tenant total, not of each
 * other: `unresolved` money sits in the total too, so the two shares are not
 * required to add up to 100 and must not be presented as if they were.
 */
export function selectTenantDepositKpis(tenantHeld) {
  const total = toNumber(tenantHeld?.total_chf);
  const cash = toNumber(tenantHeld?.by_type?.cash?.total_chf);
  const insurance = toNumber(tenantHeld?.by_type?.insurance?.total_chf);
  const bank = toNumber(tenantHeld?.by_type?.bank?.total_chf);

  return {
    total,
    cash,
    insurance,
    bank,
    tenancyCount: toNumber(tenantHeld?.tenancy_count),
    cashShare: share(cash, total),
    insuranceShare: share(insurance, total),
  };
}

/**
 * The landlord KPI figures.
 *
 * `annualPremium` is the ONLY premium this page shows in a KPI, and it is ours:
 * the annual cost of the guarantees we placed, counted monthly and lease-prorated
 * as an operating cost by `/dashboard/cost-breakdown`. It is reported beside
 * `total` and never added to it — an annual expense and a placed balance are
 * different dimensions three orders of magnitude apart.
 */
export function selectLandlordDepositKpis(landlordPlaced) {
  const total = toNumber(landlordPlaced?.total_chf);
  const insurance = toNumber(landlordPlaced?.by_type?.insurance?.total_chf);
  const bank = toNumber(landlordPlaced?.by_type?.bank?.total_chf);
  const cash = toNumber(landlordPlaced?.by_type?.cash?.total_chf);

  return {
    total,
    insurance,
    bank,
    cash,
    annualPremium: toNumber(landlordPlaced?.annual_premium_total_chf),
    unitsWithDeposit: toNumber(landlordPlaced?.units_with_deposit),
    insuranceShare: share(insurance, total),
  };
}

/**
 * The day the page is answering for, as an ISO date string, or null.
 *
 * THE OLDER OF THE TWO WINS. Both endpoints stamp their own Swiss business day
 * and normally agree; they can differ by one day when a request crosses midnight
 * in Zurich. Taking the older one means the header never claims the page is
 * fresher than its slowest source. Never the device clock — a reader in Sydney
 * must see the same Swiss business day as a reader in Zurich (Regel 51).
 */
export function selectDepositsAsOfDate(tenantHeld, landlordPlaced) {
  const dates = [tenantHeld?.as_of_date, landlordPlaced?.as_of]
    .map((d) => (typeof d === "string" && d ? d : null))
    .filter(Boolean);
  if (dates.length === 0) return null;
  // ISO dates sort lexicographically, so the smallest string is the oldest day.
  return dates.slice().sort()[0];
}

/**
 * The unresolved slice, or null when there is nothing to say.
 *
 * Money whose deposit type could not be established — no type, a type of "none",
 * an unrecognized type string, or an amount that survives only in the legacy
 * column. It IS part of `total_chf` and must stay there; what this returns is the
 * disclosure that a part of the headline is not classified. Silently dropping it
 * would understate a real liability; silently folding it into cash or insurance
 * would state a type nobody recorded.
 */
export function selectTenantUnresolvedSummary(tenantHeld) {
  const amount = toNumber(tenantHeld?.unresolved?.total_chf);
  if (amount === null || amount <= 0) return null;
  return {
    amount,
    tenancyCount: toNumber(tenantHeld?.unresolved?.tenancy_count),
  };
}
